import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';
import SeverityChip from './SeverityChip';

const AuditTab: React.FC = () => {
  const qc = useQueryClient();

  // Initial load uses cached result (max_age=3600) so the tab opens fast.
  // The "Run Audit" button forces a fresh run.
  const { data, isFetching } = useQuery({
    queryKey: ['admin-audit'],
    queryFn: () => adminAPI.getAudit(false, 3600),
    staleTime: 60000,
  });

  const refresh = useMutation({
    mutationFn: () => adminAPI.getAudit(true, 0),
    onSuccess: (result) => qc.setQueryData(['admin-audit'], result),
  });

  const running = isFetching || refresh.isPending;

  const counts = data?.counts || { CRITICAL: 0, MAJOR: 0, MINOR: 0, OK: 0 };
  const bySev = data?.by_severity || {};
  const cachedNote = data?.cached
    ? `cached — ${data?.age_seconds}s old`
    : data?.ran_at ? 'fresh' : 'not yet run';

  return (
    <Box>
      <Panel label="DB HEALTH AUDIT" sx={{ mb: 3 }}>
        <Box display="flex" gap={3} alignItems="center" mb={2} flexWrap="wrap">
          {(['CRITICAL', 'MAJOR', 'MINOR', 'OK'] as const).map((sev) => (
            <Box key={sev} display="flex" gap={1} alignItems="center">
              <SeverityChip severity={sev} />
              <Typography variant="h4" fontWeight={700}>
                {(counts[sev] ?? 0).toLocaleString()}
              </Typography>
            </Box>
          ))}
          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
            {cachedNote}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            disabled={running}
            onClick={() => refresh.mutate()}
          >
            {running ? '[ RUNNING... ]' : '[ RUN AUDIT ]'}
          </Button>
        </Box>
      </Panel>

      {(['CRITICAL', 'MAJOR', 'MINOR'] as const).map((sev) => {
        const items = bySev[sev] || [];
        if (items.length === 0) return null;
        return (
          <Panel key={sev} label={`${sev} (${items.length})`} sx={{ mb: 2 }}>
            <Box display="flex" flexDirection="column" gap={1}>
              {items.map((item: any, i: number) => (
                <Box key={i} sx={{
                  p: 1.5,
                  borderLeft: `3px solid ${sev === 'CRITICAL' ? '#ff3344' : sev === 'MAJOR' ? '#ffb000' : '#00ffff'}`,
                  backgroundColor: 'rgba(0,0,0,0.3)',
                }}>
                  <Typography variant="body2" fontWeight={600}>{item.name}</Typography>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                    {item.detail}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Panel>
        );
      })}

      {counts.OK > 0 && (
        <Panel label={`OK (${counts.OK})`}>
          <Typography variant="caption" color="textSecondary">
            {counts.OK} checks passing. Click to expand if needed — omitted by default for brevity.
          </Typography>
        </Panel>
      )}
    </Box>
  );
};

export default AuditTab;
